import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Clapperboard, Download, ImageOff, Volume2 } from "lucide-react";
import { api, mediaUrl } from "../lib/api";

export const SceneCard = ({ scene, index, projectId, imgProvider = "grok", onUpdated, onVideoStarted }) => {
  const [regenerating, setRegenerating] = useState(false);
  const [editPrompt, setEditPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState(scene.image_prompt || "");
  const imgBusy = regenerating || scene.image_status === "generating";

  const regenerate = async (useCustom) => {
    setRegenerating(true);
    try {
      await api.post(`/projects/${projectId}/scenes/${index}/image`, {
        custom_prompt: useCustom ? customPrompt : null,
        provider: imgProvider,
      });
      toast.success(`Génération de l'image de la scène ${scene.index} lancée (${imgProvider === "pollo" ? "Pollo" : "Grok"})`);
      setEditPrompt(false);
      onUpdated();
      onVideoStarted();
    } catch (e) {
      toast.error("Échec du lancement de la génération");
    } finally {
      setRegenerating(false);
    }
  };

  const generateVideo = async (provider) => {
    try {
      await api.post(`/projects/${projectId}/scenes/${index}/video`, { duration: 8, provider });
      toast.success(`Clip ${provider === "pollo" ? "Pollo (avec son)" : "Grok"} lancé pour la scène ${scene.index} (1-3 min)`);
      onUpdated();
      onVideoStarted();
    } catch (e) {
      toast.error("Échec du lancement du clip vidéo");
    }
  };

  return (
    <div
      className="bg-[#12151B]/80 backdrop-blur-xl border border-white/10 hover:border-amber-500/30 transition-all duration-300 rounded-2xl overflow-hidden"
      data-testid={`scene-card-${scene.index}`}
    >
      <div className="grid lg:grid-cols-2">
        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-black/60 border border-amber-500/30 text-amber-400 font-mono text-xs px-2.5 py-1 rounded-md tracking-widest">
              SC. {String(scene.index).padStart(2, "0")}
            </span>
            <span className="font-mono text-xs text-slate-500">~{scene.duration_sec}s</span>
          </div>
          <h3 className="font-mono text-sm tracking-wider uppercase text-amber-400 font-bold mb-3">{scene.heading}</h3>
          <p className="text-sm text-slate-300 leading-relaxed mb-4 whitespace-pre-wrap">{scene.action}</p>
          {(scene.dialogues || []).map((d, di) => (
            <div key={di} className="mb-3 px-4 sm:px-8">
              <p className="font-mono text-xs tracking-widest uppercase text-slate-300 font-semibold text-center">{d.character}</p>
              <p className="font-serif text-sm text-slate-200 leading-snug text-center">{d.line}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#090B0E] p-5 sm:p-6 flex flex-col">
          <div className="relative aspect-video rounded-xl overflow-hidden bg-black/50 border border-white/5 mb-4">
            {scene.image_url ? (
              <img src={mediaUrl(scene.image_url)} alt={scene.heading} className="w-full h-full object-cover" data-testid={`scene-image-${scene.index}`} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-600">
                <ImageOff className="w-8 h-8" />
                <span className="text-xs">Image non générée</span>
              </div>
            )}
            {imgBusy && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            )}
          </div>

          {scene.image_status === "error" && (
            <p className="mb-3 text-xs text-rose-400" data-testid={`scene-image-error-${scene.index}`}>
              {scene.image_error || "Échec de génération de l'image. Réessayez."}
            </p>
          )}

          {editPrompt && (
            <div className="mb-4">
              <textarea
                data-testid={`scene-prompt-input-${scene.index}`}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={3}
                className="w-full bg-[#12151B] border border-white/10 focus:border-amber-500/50 rounded-xl p-3 text-sm text-white outline-none resize-none"
              />
              <button
                data-testid={`scene-apply-prompt-${scene.index}`}
                onClick={() => regenerate(true)}
                disabled={imgBusy}
                className="mt-2 w-full bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold py-2.5 rounded-xl disabled:opacity-60"
              >
                Générer avec ce prompt
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-auto">
            <button
              data-testid={`scene-regenerate-image-${scene.index}`}
              onClick={() => regenerate(false)}
              disabled={imgBusy}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-slate-300 text-xs font-semibold hover:border-amber-500/40 hover:text-white transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${imgBusy ? "animate-spin" : ""}`} />
              {scene.image_url ? "Régénérer" : "Générer l'image"}
            </button>
            <button
              data-testid={`scene-edit-prompt-${scene.index}`}
              onClick={() => setEditPrompt(!editPrompt)}
              className="px-3 py-2 rounded-lg border border-white/15 text-slate-300 text-xs font-semibold hover:border-amber-500/40 hover:text-white transition-all"
            >
              Modifier le prompt
            </button>
            <button
              data-testid={`scene-generate-video-${scene.index}`}
              onClick={() => generateVideo("grok")}
              disabled={scene.video_status === "generating"}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-400 text-xs font-bold hover:bg-amber-500/25 transition-all disabled:opacity-60"
            >
              {scene.video_status === "generating" ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Clip en cours…</>
              ) : (
                <><Clapperboard className="w-3.5 h-3.5" /> Clip Grok</>
              )}
            </button>
            <button
              data-testid={`scene-generate-video-pollo-${scene.index}`}
              onClick={() => generateVideo("pollo")}
              disabled={scene.video_status === "generating"}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-500/15 border border-sky-500/40 text-sky-400 text-xs font-bold hover:bg-sky-500/25 transition-all disabled:opacity-60"
              title="Anime l'image de la scène avec son synchronisé (Pollo 2.5)"
            >
              <Volume2 className="w-3.5 h-3.5" /> Clip Pollo + son
            </button>
          </div>

          {scene.video_status === "error" && (
            <p className="mt-3 text-xs text-rose-400" data-testid={`scene-video-error-${scene.index}`}>
              {scene.video_error || "Échec de la génération du clip vidéo. Réessayez."}
            </p>
          )}
          {scene.video_url && (
            <div className="mt-4">
              <video controls className="w-full rounded-xl bg-black" src={mediaUrl(scene.video_url)} data-testid={`scene-video-player-${scene.index}`} />
              <a
                href={mediaUrl(scene.video_url)}
                download
                data-testid={`scene-video-download-${scene.index}`}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 font-semibold"
              >
                <Download className="w-3.5 h-3.5" /> Télécharger le clip
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
